import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Container} from 'src/design/atoms/Container';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconLogin} from '@tabler/icons-react';
import {useCallback, useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {useResetToPlatformClient} from 'src/app/security/authentication/useResetToPlatformClient';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppDispatch} from 'src/redux/hooks';
import {
  confirmSignInUser,
  resetSignInNextStep,
  setUsername,
  signInUser,
} from 'src/redux/slices/authSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

/* States:
Pending - The page has loaded with URL parameters but user hasn't clicked Continue yet.
Loading - The page is loading and attempting to validate the magic link token.
Success - The magic link token was validated successfully and the user is being redirected to the home page.
Error - The magic link token was invalid or expired.
Redirect - The magic link requires additional steps, redirecting to login page.
*/

type MagicLinkState = 'pending' | 'loading' | 'success' | 'error' | 'redirect';

const RETURN_URL_KEY = 'proact_post_login_return_url';

const getPostLoginRedirectUrl = (): string => {
  const storedUrl = sessionStorage.getItem(RETURN_URL_KEY);
  if (storedUrl && storedUrl.startsWith('/') && !storedUrl.startsWith('//')) {
    sessionStorage.removeItem(RETURN_URL_KEY);
    return storedUrl;
  }
  return PathUtility.Dashboard();
};

const ValidateMagicLinkPage = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const [state, setState] = useState<MagicLinkState>('pending');

  useResetToPlatformClient();

  const validateMagicLink = useCallback(async () => {
    try {
      if (!email || !token) {
        console.error('Missing email or token in URL params');
        setState('error');
        return;
      }

      setState('loading');

      // 1) Start a CUSTOM_AUTH session for the user
      const signInResult = await dispatch(
        signInUser({
          username: email,
          options: {authFlowType: 'CUSTOM_WITHOUT_SRP'},
        })
      ).unwrap();

      if (
        signInResult.nextStep.signInStep !=
        'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
      ) {
        // Redirect to login page which can handle other challenges.
        setState('redirect');
        dispatch(setUsername(email));
        navigate(PathUtility.Login(), {replace: true});
        return;
      }

      // 2) Answer challenges using the JWT until the user is signed in
      const result = await dispatch(
        confirmSignInUser({challengeResponse: token})
      ).unwrap();

      if (!result.isSignedIn) {
        const nextStep = result.nextStep?.signInStep;
        throw new Error(
          `Unsupported additional challenge: ${nextStep ?? 'UNKNOWN'}`
        );
      }

      // 3) Success -> Amplify has Cognito tokens and user is signed in
      setState('success');
      navigate(getPostLoginRedirectUrl(), {replace: true});
    } catch (error) {
      console.error('Error validating magic link', error);
      setState('error');
      dispatch(resetSignInNextStep());
    }
  }, [email, navigate, token, dispatch]);

  const handleContinue = () => {
    void validateMagicLink();
  };

  // Check if we have the required parameters
  useEffect(() => {
    if (!email || !token) {
      setState('error');
    }
  }, [email, token]);

  return (
    <Container size={450} my={40}>
      <Stack>
        <Title ta="center" order={2}>
          Passwordless Login
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Login via email
        </Text>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack>
            {state === 'pending' && (
              <Stack>
                <Alert color="green" title="Welcome back!" icon={<IconLogin />}>
                  Click the button below to continue with the sign-in process.
                </Alert>
                <Button
                  onClick={handleContinue}
                  aria-label="continue with magic link button">
                  Continue
                </Button>
              </Stack>
            )}
            {state === 'loading' && (
              <Group align="center" justify="center">
                <Text c="dimmed" size="sm">
                  Validating magic link...
                </Text>
                <Loader size="xs" />
              </Group>
            )}
            {state === 'success' && (
              <Stack>
                <Text c="dimmed" size="sm">
                  Magic link successfully validated! You will be redirected to
                  the home page in a moment.
                </Text>
              </Stack>
            )}
            {state === 'redirect' && (
              <Stack>
                <Text c="dimmed" size="sm">
                  Redirecting to login page to complete authentication...
                </Text>
                <Loader size="xs" />
              </Stack>
            )}
            {state === 'error' && (
              <>
                <ServerErrorAlert
                  message={
                    'An error occurred while validating the magic link. It may have expired or been used already. If you enabled authenticator MFA, return to login and use your password and authenticator code.'
                  }
                />
                <Button
                  mt="xl"
                  component={Link}
                  to={PathUtility.Login()}
                  aria-label="back to login button">
                  Back to login
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

// Register the route
routeRegistry.registerPageRoute({
  backNavigation: 'transient',
  path: PathUtility.ValidateMagicLink(),
  element: <ValidateMagicLinkPage />,
  isPrivate: false,
});

export {ValidateMagicLinkPage};
